import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Rate } from '@tinper/next-ui';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link, useParams } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { Breadcrumb, EmptyState, IconBadge, MarketVisual, MetricIcon, } from '../components/marketplace/MarketplaceShared';
import { VersionChangeLogContent } from '../components/marketplace/VersionChangeLogContent';
import { getPluginDetail, getPluginVersionHistory } from '../services/marketplaceApi';
import './marketplace.scss';
const FRONTMATTER_RE = /^\s*---\s*\n[\s\S]*?\n---\s*(?:\n|$)/;
const CRITICAL_BLOCK_RE = /<critical_system_prerequisite[^>]*>[\s\S]*?<\/critical_system_prerequisite>/gi;
const tabs = [
    { label: '概述', value: 'overview' },
    { label: '插件内容', value: 'content' },
    { label: '版本历史', value: 'versions' },
];
function cleanPluginDocument(markdown) {
    return markdown
        .replace(/^\uFEFF/, '')
        .replace(FRONTMATTER_RE, '')
        .replace(CRITICAL_BLOCK_RE, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
function resolveDocumentAssetUrl(url, baseUrl) {
    if (!url || !baseUrl || url.startsWith('http') || url.startsWith('/') || url.startsWith('#')) {
        return url;
    }
    try {
        return new URL(url, baseUrl).toString();
    }
    catch {
        return url;
    }
}
function createPluginDocumentComponents(baseUrl) {
    return {
        a: ({ href, children, node: _node, ...props }) => {
            const resolvedHref = resolveDocumentAssetUrl(href, baseUrl);
            return (_jsx("a", { href: resolvedHref, target: "_blank", rel: "noopener noreferrer", ...props, children: children }));
        },
        img: ({ src, alt, node: _node, ...props }) => {
            return _jsx("img", { src: resolveDocumentAssetUrl(src, baseUrl), alt: alt || '', ...props });
        },
    };
}
function formatCount(value) {
    if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}K`;
    }
    return String(value);
}
function formatRating(value) {
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
function hasRating(value) {
    return typeof value === 'number';
}
function formatHistoryDate(value) {
    if (!value)
        return '';
    return value.split(' ')[0].replace(/-/g, '/');
}
function PluginContentSection({ title, items }) {
    if (!items.length)
        return null;
    return (_jsxs("section", { className: "plugin-content-section", children: [_jsx("div", { className: "plugin-content-section__label", children: title }), _jsx("div", { className: "plugin-content-table-wrap", children: _jsxs("table", { className: "plugin-content-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "\u7F16\u7801" }), _jsx("th", { children: "\u540D\u79F0" }), _jsx("th", { children: "\u63CF\u8FF0" })] }) }), _jsx("tbody", { children: items.map((item) => (_jsxs("tr", { children: [_jsx("td", { children: item.code || '-' }), _jsx("td", { children: item.name }), _jsx("td", { children: item.description })] }, `${item.code || item.name}-${item.description}`))) })] }) })] }));
}
export default function PluginDetailPage() {
    const { id = '' } = useParams();
    const [plugin, setPlugin] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [documentMarkdown, setDocumentMarkdown] = useState('');
    const [documentLoading, setDocumentLoading] = useState(false);
    const [documentError, setDocumentError] = useState('');
    const [versionHistory, setVersionHistory] = useState([]);
    const [versionHistoryLoading, setVersionHistoryLoading] = useState(false);
    const [versionHistoryError, setVersionHistoryError] = useState('');
    const [collapsedVersionRecords, setCollapsedVersionRecords] = useState({});
    useEffect(() => {
        let active = true;
        setLoading(true);
        setActiveTab('overview');
        setDocumentMarkdown('');
        setDocumentError('');
        setDocumentLoading(false);
        setVersionHistory([]);
        setVersionHistoryError('');
        setVersionHistoryLoading(false);
        setCollapsedVersionRecords({});
        getPluginDetail(id).then((res) => {
            if (!active)
                return;
            setPlugin(res);
            setLoading(false);
            window.scrollTo({ top: 0, behavior: 'auto' });
        });
        return () => {
            active = false;
        };
    }, [id]);
    const changeLogSource = plugin?.changeLog || plugin?.versions[0]?.changeLog || '';
    useEffect(() => {
        if (!plugin?.packageName || changeLogSource) {
            setVersionHistory([]);
            setVersionHistoryError('');
            setVersionHistoryLoading(false);
            return;
        }
        let active = true;
        setVersionHistory([]);
        setVersionHistoryError('');
        setVersionHistoryLoading(true);
        getPluginVersionHistory(plugin.packageName)
            .then((items) => {
            if (!active)
                return;
            setVersionHistory(items);
            setVersionHistoryLoading(false);
        })
            .catch((error) => {
            if (!active)
                return;
            console.error(error);
            setVersionHistoryError('版本历史暂时无法加载');
            setVersionHistoryLoading(false);
        });
        return () => {
            active = false;
        };
    }, [plugin?.packageName, changeLogSource]);
    useEffect(() => {
        if (!plugin?.documentUrl) {
            setDocumentMarkdown('');
            setDocumentError('');
            setDocumentLoading(false);
            return;
        }
        const controller = new AbortController();
        let active = true;
        setDocumentMarkdown('');
        setDocumentError('');
        setDocumentLoading(true);
        fetch(plugin.documentUrl, { signal: controller.signal })
            .then((response) => {
            if (!response.ok) {
                throw new Error(`Document request failed: ${response.status}`);
            }
            return response.text();
        })
            .then((markdown) => {
            if (!active)
                return;
            setDocumentMarkdown(cleanPluginDocument(markdown));
            setDocumentLoading(false);
        })
            .catch((error) => {
            if (!active || error.name === 'AbortError')
                return;
            console.error(error);
            setDocumentError('文档暂时无法加载');
            setDocumentLoading(false);
        });
        return () => {
            active = false;
            controller.abort();
        };
    }, [plugin?.documentUrl]);
    if (loading) {
        return (_jsxs(_Fragment, { children: [_jsx("main", { className: "market-page", children: _jsx("div", { className: "market-shell", children: _jsx("div", { className: "market-loading", children: "\u52A0\u8F7D\u63D2\u4EF6\u8BE6\u60C5\u4E2D..." }) }) }), _jsx(Footer, {})] }));
    }
    if (!plugin) {
        return (_jsxs(_Fragment, { children: [_jsx("main", { className: "market-page", children: _jsxs("div", { className: "market-shell", children: [_jsx(EmptyState, { title: "\u672A\u627E\u5230\u63D2\u4EF6", description: "\u8BE5\u63D2\u4EF6\u53EF\u80FD\u5DF2\u4E0B\u7EBF\uFF0C\u60A8\u53EF\u4EE5\u8FD4\u56DE\u63D2\u4EF6\u5E02\u573A\u67E5\u770B\u5176\u4ED6\u63D2\u4EF6\u3002" }), _jsx("div", { className: "market-empty-action", children: _jsx(Link, { to: "/plugins", children: "\u8FD4\u56DE\u63D2\u4EF6\u5E02\u573A" }) })] }) }), _jsx(Footer, {})] }));
    }
    const hasContent = Boolean(plugin.workflows.length || plugin.agents.length || plugin.skills.length || plugin.tools.length);
    const pluginAuthor = plugin.author || '';
    const latestVersion = plugin.version || plugin.versions[0]?.version;
    const documentComponents = createPluginDocumentComponents(plugin.documentUrl);
    const toggleVersionRecord = (recordKey) => {
        setCollapsedVersionRecords((prev) => ({
            ...prev,
            [recordKey]: !prev[recordKey],
        }));
    };
    const visibleTabs = tabs.filter((tab) => {
        if (tab.value === 'content')
            return hasContent;
        return true;
    });
    const resolvedActiveTab = visibleTabs.some((tab) => tab.value === activeTab)
        ? activeTab
        : visibleTabs[0]?.value;
    return (_jsxs(_Fragment, { children: [_jsx("main", { className: "market-page market-detail-page", children: _jsxs("div", { className: "market-shell", children: [_jsx(Breadcrumb, { items: [
                                { label: '首页', to: '/' },
                                { label: '插件市场', to: '/plugins' },
                                { label: '插件详情' },
                            ] }), _jsxs("section", { className: "market-detail-hero plugin-detail-hero", children: [_jsxs("div", { className: "market-detail-identity", children: [_jsx(IconBadge, { icon: "plugin", color: plugin.color, label: plugin.name, imageSrc: plugin.iconUrl }), _jsxs("div", { children: [_jsx("h1", { children: plugin.name }), plugin.typeLabels.length > 0 && (_jsx("div", { className: "market-tags", children: _jsx("span", { children: plugin.typeLabels.join('、') }) })), plugin.description && _jsx("p", { children: plugin.description })] })] }), _jsx(MarketVisual, { type: "plugins", variant: "detail" })] }), _jsxs("section", { className: "plugin-metric-grid", "aria-label": "\u63D2\u4EF6\u6307\u6807", children: [_jsxs("div", { className: "plugin-metric-item", children: [_jsxs("span", { children: [_jsx(MetricIcon, { type: "views" }), "\u8BBF\u95EE\u91CF"] }), _jsx("strong", { children: formatCount(plugin.views) })] }), hasRating(plugin.rating) && (_jsxs("div", { className: "plugin-metric-item", children: [_jsxs("span", { children: [_jsx(MetricIcon, { type: "rating" }), "\u8BC4\u5206"] }), _jsxs("strong", { className: "market-rating market-rating--metric", "aria-label": `评分 ${formatRating(plugin.rating)} 分，满分 5 分`, children: [_jsx(Rate, { browser: true, allowHalf: true, count: 5, value: plugin.rating }), _jsxs("em", { children: [formatRating(plugin.rating), "\u5206"] })] })] })), pluginAuthor && (_jsxs("div", { className: "plugin-metric-item", children: [_jsxs("span", { children: [_jsx(MetricIcon, { type: "author" }), "\u4F5C\u8005"] }), _jsx("strong", { children: pluginAuthor })] })), plugin.updatedAt && (_jsxs("div", { className: "plugin-metric-item", children: [_jsxs("span", { children: [_jsx(MetricIcon, { type: "date" }), "\u66F4\u65B0\u65F6\u95F4"] }), _jsx("strong", { children: plugin.updatedAt })] }))] }), visibleTabs.length > 0 && (_jsxs("section", { className: "plugin-tabs-card", children: [_jsx("div", { className: "plugin-tabs", role: "tablist", "aria-label": "\u63D2\u4EF6\u8BE6\u60C5\u5185\u5BB9", children: visibleTabs.map((tab) => (_jsx("button", { className: resolvedActiveTab === tab.value ? 'is-active' : '', type: "button", role: "tab", "aria-selected": resolvedActiveTab === tab.value, onClick: () => setActiveTab(tab.value), children: tab.label }, tab.value))) }), resolvedActiveTab === 'overview' && (_jsxs("article", { className: "market-rich-card market-rich-card--plain skill-detail-tab-panel", role: "tabpanel", children: [documentLoading && _jsx("div", { className: "skill-document-state", children: "\u52A0\u8F7D\u6587\u6863\u4E2D..." }), !documentLoading && documentError && _jsx("div", { className: "skill-document-state", children: documentError }), !documentLoading && !documentError && documentMarkdown && (_jsx("div", { className: "skill-markdown", children: _jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], components: documentComponents, children: documentMarkdown }) })), !documentLoading && !documentError && !documentMarkdown && (_jsx("div", { className: "skill-document-state", children: "\u6682\u65E0\u6982\u8FF0\u5185\u5BB9" }))] })), resolvedActiveTab === 'content' && hasContent && (_jsx("article", { className: "market-rich-card market-rich-card--plain", children: _jsxs("div", { className: "plugin-content-sections", children: [_jsx(PluginContentSection, { title: "AutoPilots \u81EA\u52A8\u5316\u6D41\u7A0B", items: plugin.workflows }), _jsx(PluginContentSection, { title: "Agent \u667A\u80FD\u4F53", items: plugin.agents }), _jsx(PluginContentSection, { title: "Skills\u6280\u80FD", items: plugin.skills }), _jsx(PluginContentSection, { title: "Tools\u5DE5\u5177", items: plugin.tools })] }) })), resolvedActiveTab === 'versions' && (_jsx("article", { className: "market-rich-card market-rich-card--plain skill-detail-tab-panel", role: "tabpanel", children: changeLogSource ? (_jsx(VersionChangeLogContent, { source: changeLogSource, variant: "full", loadingText: "\u52A0\u8F7D\u7248\u672C\u5386\u53F2\u4E2D...", errorText: "\u7248\u672C\u5386\u53F2\u6682\u65F6\u65E0\u6CD5\u52A0\u8F7D", emptyText: "\u6682\u65E0\u7248\u672C\u5386\u53F2" })) : versionHistoryLoading ? (_jsx("div", { className: "skill-document-state", children: "\u52A0\u8F7D\u7248\u672C\u5386\u53F2\u4E2D..." })) : versionHistoryError ? (_jsx("div", { className: "skill-document-state", children: versionHistoryError })) : versionHistory.length > 0 ? (_jsx("div", { className: "skill-version-timeline", children: versionHistory.map((item, index) => {
                                            const isLatest = item.version === latestVersion || index === 0;
                                            const createdAt = formatHistoryDate(item.createdAt);
                                            const recordKey = `${item.id}-${item.version}`;
                                            const isCollapsed = Boolean(collapsedVersionRecords[recordKey]);
                                            const historyText = item.changeLog || '';
                                            return (_jsx("div", { className: "skill-version-record", children: _jsxs("div", { className: "skill-version-record__main", children: [_jsx("span", { className: "skill-version-dot", "aria-hidden": "true" }), _jsxs("div", { className: "skill-version-record__content", children: [_jsxs("div", { className: "skill-version-record__head", children: [_jsxs("div", { className: "skill-version-record__title", children: [_jsxs("strong", { children: ["v", item.version] }), isLatest && _jsx("span", { className: "skill-version-latest", children: "\u6700\u65B0" })] }), _jsxs("div", { className: "skill-version-record__actions", children: [createdAt && _jsx("time", { dateTime: item.createdAt, children: createdAt }), historyText && (_jsx("button", { className: isCollapsed ? 'is-collapsed' : '', type: "button", "aria-label": isCollapsed ? '展开版本说明' : '收起版本说明', "aria-expanded": !isCollapsed, onClick: () => toggleVersionRecord(recordKey), children: _jsx("span", { "aria-hidden": "true" }) }))] })] }), historyText && !isCollapsed && _jsx(VersionChangeLogContent, { source: historyText })] })] }) }, recordKey));
                                        }) })) : (_jsx("div", { className: "skill-document-state", children: "\u6682\u65E0\u7248\u672C\u5386\u53F2" })) }))] }))] }) }), _jsx(Footer, {})] }));
}
